/**
 * NotificationScheduleCard Component
 *
 * Unified card containing both delivery settings and weekly digest sections.
 * Displays sections side-by-side on desktop, stacked on mobile.
 *
 * Design:
 * - Single Card wrapper for unified appearance
 * - Two distinct sections with visual separation via background tint
 * - Responsive: horizontal on desktop, vertical on mobile
 */

import { Card, Box } from '@shopify/polaris';
import { DeliverySettingsSection } from '../sections/DeliverySettingsSection';
import { WeeklyDigestSection } from '../sections/WeeklyDigestSection';
import type { DeliverySettingsSectionProps } from '../sections/DeliverySettingsSection';
import type { WeeklyDigestSectionProps } from '../sections/WeeklyDigestSection';

export interface NotificationScheduleCardProps {
  // Delivery settings props
  timezone: DeliverySettingsSectionProps['timezone'];
  onTimezoneChange: DeliverySettingsSectionProps['onTimezoneChange'];
  quietHoursEnabled: DeliverySettingsSectionProps['quietHoursEnabled'];
  quietHoursStart: DeliverySettingsSectionProps['quietHoursStart'];
  quietHoursEnd: DeliverySettingsSectionProps['quietHoursEnd'];
  onQuietHoursEnabledChange: DeliverySettingsSectionProps['onQuietHoursEnabledChange'];
  onQuietHoursStartChange: DeliverySettingsSectionProps['onQuietHoursStartChange'];
  onQuietHoursEndChange: DeliverySettingsSectionProps['onQuietHoursEndChange'];
  // Weekly digest props
  weeklyDigestDay: WeeklyDigestSectionProps['weeklyDigestDay'];
  weeklyDigestTime: WeeklyDigestSectionProps['weeklyDigestTime'];
  weeklyDigestSections: WeeklyDigestSectionProps['weeklyDigestSections'];
  onWeeklyDigestDayChange: WeeklyDigestSectionProps['onWeeklyDigestDayChange'];
  onWeeklyDigestTimeChange: WeeklyDigestSectionProps['onWeeklyDigestTimeChange'];
  onWeeklyDigestSectionsChange: WeeklyDigestSectionProps['onWeeklyDigestSectionsChange'];
  shopDomain?: WeeklyDigestSectionProps['shopDomain'];
}

export function NotificationScheduleCard({
  // Delivery settings
  timezone,
  onTimezoneChange,
  quietHoursEnabled,
  quietHoursStart,
  quietHoursEnd,
  onQuietHoursEnabledChange,
  onQuietHoursStartChange,
  onQuietHoursEndChange,
  // Weekly digest
  weeklyDigestDay,
  weeklyDigestTime,
  weeklyDigestSections,
  onWeeklyDigestDayChange,
  onWeeklyDigestTimeChange,
  onWeeklyDigestSectionsChange,
  shopDomain,
}: NotificationScheduleCardProps) {
  return (
    <Card padding="0">
      <div className="notification-schedule-grid">
        {/* Delivery Settings Section */}
        <Box padding="400">
          <DeliverySettingsSection
            timezone={timezone}
            onTimezoneChange={onTimezoneChange}
            quietHoursEnabled={quietHoursEnabled}
            quietHoursStart={quietHoursStart}
            quietHoursEnd={quietHoursEnd}
            onQuietHoursEnabledChange={onQuietHoursEnabledChange}
            onQuietHoursStartChange={onQuietHoursStartChange}
            onQuietHoursEndChange={onQuietHoursEndChange}
          />
        </Box>

        {/* Weekly Digest Section */}
        <Box padding="400">
          <WeeklyDigestSection
            weeklyDigestDay={weeklyDigestDay}
            weeklyDigestTime={weeklyDigestTime}
            weeklyDigestSections={weeklyDigestSections}
            onWeeklyDigestDayChange={onWeeklyDigestDayChange}
            onWeeklyDigestTimeChange={onWeeklyDigestTimeChange}
            onWeeklyDigestSectionsChange={onWeeklyDigestSectionsChange}
            shopDomain={shopDomain}
          />
        </Box>
      </div>
    </Card>
  );
}
